import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQuery, useMutation } from "@apollo/client";
import sha256 from 'crypto-js/sha256';
import enc from 'crypto-js/enc-hex';

// material
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Stack,
  IconButton,
  InputAdornment,
  InputLabel,
  FormControl,
} from "@mui/material";

// hooks
import useResponsive from "../../hooks/useResponsive";
// components
import Iconify from "../../components/Iconify";
import {
  FormProvider,
  RHFTextField,
  RHFToggleButton,
  RHFSelect,
} from "../../components/hook-form";

// info
import { ADMIN } from "../../info/ADMIN";
import { AdminGQLFunction } from "../../graphql/ADMIN";

import PHONE_REG_EXP from '../../components/hook-form/regrex';


export default function EditAdminForm({ adminId }) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const smUp = useResponsive("up", "sm");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const EditAdminSchema = Yup.object().shape({
    email: Yup.string()
      .email(t('login.email_valid'))
      .required(t('people.email_req')),
    password: Yup.string(),
    confirmPassword: Yup.string().oneOf(
      [Yup.ref("password"), null],
      t('login.password_match')
    ),
    username: Yup.string().required(t('people.username_req')),
    telephone: Yup.string()
      .matches(PHONE_REG_EXP, t('people.phone_invalid'))
      .nullable(true),
  });

  const defaultValues = {
    id: '',
    email: '',
    password: '',
    confirmPassword: '',
    username: '',
    telephone: '',
    userState: 0
  };

  const methods = useForm({
    resolver: yupResolver(EditAdminSchema),
    defaultValues,
  });
  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
    setError,
  } = methods;

  const { loading: queryLoading, data: queryData } = useQuery(AdminGQLFunction.GET_ADMIN_INFO, {
    variables: { id: adminId },
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (queryData && queryData.adminInfo) {
      const info = queryData.adminInfo;
      reset({
        id: info.id || "",
        email: info.email || "",
        password: "",
        confirmPassword: "",
        username: info.username || "",
        telephone: info.telephone || "",
        userState: info.userState || 0,
      });
    }
  }, [queryData, reset]);

  const [updateadmin] = useMutation(AdminGQLFunction.UPDATE_ADMIN);

  const onSubmit = async (input) => {
    try {
      const { data } = await updateadmin({
        variables: {
          id: adminId,
          email: input.email,
          password: input.password ? sha256(input.password).toString(enc) : "",
          username: input.username,
          telephone: input.telephone,
          userState: input.userState,
        },
      });

      const info = data.updateAdmin;
      if (info.err) {
        setError(info.err, { type: "custom", message: info.msg });
      } else {
        enqueueSnackbar(t("common.success"), { variant: "success" });
        navigate(`/admin/info/${adminId}`, { replace: true });
      }
    } catch (error) {
      enqueueSnackbar(error.message, { variant: "error" });
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Box
        sx={{
          display: "grid",
          columnGap: 2,
          rowGap: 3,
          gridTemplateColumns: smUp ? "repeat(2, 1fr)" : "1fr",
          mt: 1,
          mb: 4,
        }}
      >
        <RHFTextField 
          name="id" 
          label={t('common.id')} 
          disable 
          loading={queryLoading} 
        />
        <RHFTextField
          name="username"
          label={t('common.username')}
          loading={queryLoading}
        />
        <RHFTextField
          name="email"
          label={t('common.email')}
          loading={queryLoading}
        />
        <RHFTextField
          name="telephone"
          label={t('common.telephone')}
          loading={queryLoading}
        />
        <RHFTextField
          name="password"
          label={t('people.password')}
          type={showPassword ? "text" : "password"}
          loading={queryLoading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  <Iconify
                    icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <RHFTextField
          name="confirmPassword"
          label={t('people.confirm_password')}
          type={showPassword ? "text" : "password"}
          loading={queryLoading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  <Iconify
                    icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {smUp ? (
          <FormControl sx={{ gridColumn: "span 2" }}>
            <Box sx={{ mb: 2 }}>
              <InputLabel shrink>{t('people.account_state')}</InputLabel>
            </Box>
            <RHFToggleButton
              states={ADMIN.ADMIN_STATE.map(state => t(`common.${state.toLowerCase()}`))}
              name="userState"
              loading={queryLoading}
            />
          </FormControl>
        ) : (
          <FormControl>
            <RHFSelect
              label={t('people.account_state')}
              states={ADMIN.ADMIN_STATE.map(state => t(`common.${state.toLowerCase()}`))}
              name="userState"
              loading={queryLoading}
            />
          </FormControl>
        )}
      </Box>
      <LoadingButton
        sx={{ maxWidth: "200px" }}
        fullWidth
        type="submit"
        variant="contained"
        loading={isSubmitting}
      >
        {t('common.save')}
      </LoadingButton>
    </FormProvider>
  );
}
